import { ConnectionSvg } from '../illustrations/connection.js';
import { EmptySvg } from '../illustrations/empty.js';
import { FileSearchingSvg } from '../illustrations/file-searching.js';
import { FixingBugsSvg } from '../illustrations/fixing-bugs.js';
import { GoingOfflineSvg } from '../illustrations/going-offline.js';
import { LostSvg } from '../illustrations/lost.js';
import { MaintenanceSvg } from '../illustrations/maintenance.js';
import { NoDataSvg } from '../illustrations/no-data.js';
import { PageNotFoundSvg } from '../illustrations/page-not-found.js';
import { ServerDownSvg } from '../illustrations/server-down.js';
import { VoidSvg } from '../illustrations/void.js';
import { WarningSvg } from '../illustrations/warning.js';
import { cx } from '../utils/cx.js';
import styles from './StateIllustration.module.css';

export type StateIllustrationVariant =
  | 'error'
  | 'not-found'
  | 'empty'
  | 'void'
  | 'warning'
  | 'offline'
  | 'maintenance'
  | 'no-data'
  | 'lost'
  | 'connection'
  | 'search'
  | 'fixing';

const SVG_BY_VARIANT: Record<StateIllustrationVariant, string> = {
  error: ServerDownSvg,
  'not-found': PageNotFoundSvg,
  empty: EmptySvg,
  void: VoidSvg,
  warning: WarningSvg,
  offline: GoingOfflineSvg,
  maintenance: MaintenanceSvg,
  'no-data': NoDataSvg,
  lost: LostSvg,
  connection: ConnectionSvg,
  search: FileSearchingSvg,
  fixing: FixingBugsSvg,
};

export type StateIllustrationProps = {
  variant: StateIllustrationVariant;
  size?: 'sm' | 'md' | 'lg';
  tone?: 'soft' | 'default';
  className?: string;
};

const SIZE_PX = { sm: 160, md: 220, lg: 360 } as const;

function readViewBox(svg: string): { w: number; h: number } | null {
  const m = svg.match(/viewBox=["'](?:0\s+0\s+)?([0-9.]+)\s+([0-9.]+)["']/i);
  if (!m) return null;
  const w = Number(m[1]);
  const h = Number(m[2]);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return null;
  return { w, h };
}

/** Strip root SVG width/height so first paint cannot expand to intrinsic size. */
function prepareSvgMarkup(svg: string): string {
  return svg
    .replace(/<svg\b([^>]*)>/i, (_, attrs: string) => {
      const cleaned = attrs.replace(/\s(?:width|height)="[^"]*"/gi, '');
      return `<svg${cleaned} style="display:block;width:100%;height:100%;max-width:100%">`;
    });
}

/** unDraw illustrations — Hamssah palette, transparent backdrops. */
export function StateIllustration({ variant, size = 'md', tone = 'default', className }: StateIllustrationProps) {
  const px = SIZE_PX[size];
  const raw = SVG_BY_VARIANT[variant];
  const box = readViewBox(raw);
  const svg = prepareSvgMarkup(raw);
  return (
    <div
      className={cx(
        styles.root,
        size === 'sm' && styles.sizeSm,
        size === 'md' && styles.sizeMd,
        size === 'lg' && styles.sizeLg,
        tone === 'soft' && styles.toneSoft,
        className,
      )}
      style={{
        width: `min(${px}px, 100%)`,
        maxWidth: '100%',
        marginInline: 'auto',
        overflow: 'hidden',
        lineHeight: 0,
        ...(box ? { aspectRatio: `${box.w} / ${box.h}` } : null),
      }}
      aria-hidden
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}
